import React, { useState, useEffect } from "react";
import { Shield, RefreshCw, Clock, AlertTriangle, Key, Package } from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";

/**
 * CodeSecurityMetrics component displays high-level metrics from security reports.
 * Shows summary statistics for both SAST and Secret Detection reports.
 */
export default function CodeSecurityMetrics() {
  const [sastReport, setSastReport] = useState(null);
  const [secretReport, setSecretReport] = useState(null);
  const [dependencyReport, setDependencyReport] = useState(null);
  const [sastReportLoading, setSastReportLoading] = useState(false);
  const [secretReportLoading, setSecretReportLoading] = useState(false);
  const [dependencyReportLoading, setDependencyReportLoading] = useState(false);
  
  // Fetch all security reports when component mounts
  useEffect(() => {
    fetchSastReport();
    fetchSecretReport();
    fetchDependencyReport();
  }, []);
  
  // Function to fetch the SAST report
  const fetchSastReport = async () => {
    try {
      setSastReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.SAST_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setSastReport(response.data);
      } else {
        setSastReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setSastReportLoading(false);
    } catch (error) {
      setSastReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setSastReportLoading(false);
    }
  };
  
  // Function to fetch the Secret Detection report
  const fetchSecretReport = async () => {
    try {
      setSecretReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.SECRET_DETECTION_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setSecretReport(response.data);
      } else {
        setSecretReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setSecretReportLoading(false);
    } catch (error) {
      setSecretReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setSecretReportLoading(false);
    }
  };
  
  // Function to fetch the Dependency Scanning report
  const fetchDependencyReport = async () => {
    try {
      setDependencyReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.DEPENDENCY_SCANNING_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setDependencyReport(response.data);
      } else {
        setDependencyReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setDependencyReportLoading(false);
    } catch (error) {
      setDependencyReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setDependencyReportLoading(false);
    }
  };
  
  // Calculate report stats
  const calculateReportStats = (report) => {
    // Check if report exists and has the correct structure
    if (!report || !report.report) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    // SAST and Secret Detection reports have a 'vulnerabilities' array
    const vulnerabilities = report.report.vulnerabilities || report.report;
    if (!Array.isArray(vulnerabilities)) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    const stats = { total: vulnerabilities.length, high: 0, medium: 0, low: 0, info: 0 };
    
    vulnerabilities.forEach(vulnerability => {
      if (vulnerability.severity) {
        const severity = vulnerability.severity.toLowerCase();
        if (severity === 'critical' || severity === 'high') {
          stats.high++;
        } else if (severity === 'medium') {
          stats.medium++;
        } else if (severity === 'low') {
          stats.low++;
        } else {
          stats.info++;
        }
      }
    });
    
    return stats;
  };
  
  // Calculate stats for all reports
  const sastStats = calculateReportStats(sastReport);
  const secretStats = calculateReportStats(secretReport);
  const dependencyStats = calculateReportStats(dependencyReport);
  
  // Calculate total vulnerabilities across all reports
  const totalVulnerabilities = sastStats.total + secretStats.total + dependencyStats.total;
  const totalHigh = sastStats.high + secretStats.high + dependencyStats.high;
  const totalMedium = sastStats.medium + secretStats.medium + dependencyStats.medium;
  const totalLow = sastStats.low + secretStats.low + dependencyStats.low;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {/* SAST Card */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-50 text-green-600 mr-4">
              <Shield size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Security Vulnerabilities</h2>
              <p className="text-sm text-gray-500">SAST analysis</p>
            </div>
          </div>
          <div>
            <button 
              onClick={fetchSastReport}
              disabled={sastReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh SAST report"
            >
              <RefreshCw size={18} className={sastReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
        
        {sastReportLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse flex flex-col items-center">
              <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        ) : sastReport?.status === 'error' ? (
          <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700">
            <div className="flex mb-2">
              <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
              <h3 className="font-medium">Error retrieving report</h3>
            </div>
            <p className="text-sm">{sastReport.message || 'Unable to fetch SAST metrics'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-800">{sastStats.total}</div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{sastStats.high}</div>
                <div className="text-sm text-red-600">High</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-orange-600">{sastStats.medium}</div>
                <div className="text-sm text-orange-600">Medium</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{sastStats.low}</div>
                <div className="text-sm text-yellow-600">Low</div>
              </div>
            </div>
            {sastReport?.timestamp && (
              <div className="flex items-center text-xs text-gray-500 mt-3">
                <Clock size={12} className="mr-1" />
                Last updated: {new Date(sastReport.timestamp).toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Secret Detection Card */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-indigo-50 text-indigo-600 mr-4">
              <Key size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Secret Detection</h2>
              <p className="text-sm text-gray-500">Exposed credentials & tokens</p>
            </div>
          </div>
          <div>
            <button 
              onClick={fetchSecretReport}
              disabled={secretReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh Secret Detection report"
            >
              <RefreshCw size={18} className={secretReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
        
        {secretReportLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse flex flex-col items-center">
              <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        ) : secretReport?.status === 'error' ? (
          <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700">
            <div className="flex mb-2">
              <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
              <h3 className="font-medium">Error retrieving report</h3>
            </div>
            <p className="text-sm">{secretReport.message || 'Unable to fetch Secret Detection metrics'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-800">{secretStats.total}</div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{secretStats.high}</div>
                <div className="text-sm text-red-600">High</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-orange-600">{secretStats.medium}</div>
                <div className="text-sm text-orange-600">Medium</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{secretStats.low}</div>
                <div className="text-sm text-yellow-600">Low</div>
              </div>
            </div>
            {secretReport?.timestamp && (
              <div className="flex items-center text-xs text-gray-500 mt-3">
                <Clock size={12} className="mr-1" />
                Last updated: {new Date(secretReport.timestamp).toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Dependency Scanning Card */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
              <Package size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Dependency Vulnerabilities</h2>
              <p className="text-sm text-gray-500">Vulnerable third-party packages</p>
            </div>
          </div>
          <div>
            <button 
              onClick={fetchDependencyReport}
              disabled={dependencyReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh Dependency Scanning report"
            >
              <RefreshCw size={18} className={dependencyReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
        
        {dependencyReportLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse flex flex-col items-center">
              <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        ) : dependencyReport?.status === 'error' ? (
          <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700">
            <div className="flex mb-2">
              <AlertTriangle size={18} className="mr-2 flex-shrink-0" />
              <h3 className="font-medium">Error retrieving report</h3>
            </div>
            <p className="text-sm">{dependencyReport.message || 'Unable to fetch Dependency Scanning metrics'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-800">{dependencyStats.total}</div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{dependencyStats.high}</div>
                <div className="text-sm text-red-600">High</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-orange-600">{dependencyStats.medium}</div>
                <div className="text-sm text-orange-600">Medium</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{dependencyStats.low}</div>
                <div className="text-sm text-yellow-600">Low</div>
              </div>
            </div>
            {dependencyReport?.timestamp && (
              <div className="flex items-center text-xs text-gray-500 mt-3">
                <Clock size={12} className="mr-1" />
                Last updated: {new Date(dependencyReport.timestamp).toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
